/**
 * Line Chart Component
 * Shows daily issued vs redeemed values with hover tooltips
 * Works for any date range (adapts to 1 day or 365+ days)
 */

import { useState } from "react";

interface ChartData {
  date: string; // YYYY-MM-DD
  issuedValue: number;
  redeemedValue: number;
  redemptionCount: number;
  issuedCount?: number; // Count of issued vouchers
}

interface LineChartProps {
  data: ChartData[];
  height?: number;
}

export default function LineChart({ data, height = 300 }: LineChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number;
    y: number;
    data: ChartData;
  } | null>(null);

  if (data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Daily Activity
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No data available for the selected period
        </div>
      </div>
    );
  }

  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.issuedValue, d.redeemedValue)),
    100, // Minimum scale
  );

  const padding = { top: 20, right: 40, bottom: 60, left: 60 };
  const width = Math.max(800, data.length * 40); // Adapt width to data points
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate point positions
  const getX = (index: number) =>
    padding.left + (index / Math.max(data.length - 1, 1)) * chartWidth;
  const getY = (value: number) =>
    padding.top + chartHeight - (value / maxValue) * chartHeight;

  // Format date based on range
  const formatDate = (dateStr: string, totalDays: number) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    if (totalDays <= 7) {
      // Show day of week for weekly view
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } else if (totalDays <= 31) {
      // Show month/day for monthly view
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } else {
      // Show month/year for longer ranges
      return date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    }
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Generate path for line
  const generatePath = (values: number[]) => {
    return data
      .map((_, index) => {
        const x = getX(index);
        const y = getY(values[index]);
        return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(" ");
  };

  const issuedPath = generatePath(data.map((d) => d.issuedValue));
  const redeemedPath = generatePath(data.map((d) => d.redeemedValue));

  // Determine how many X-axis labels to show based on data length
  const getXAxisLabelInterval = () => {
    if (data.length <= 7) {
      return 1;
    } // Show all for weekly
    if (data.length <= 14) {
      return 2;
    } // Every other day
    if (data.length <= 31) {
      return Math.ceil(data.length / 7);
    } // ~weekly intervals
    return Math.ceil(data.length / 10); // ~10 labels max
  };

  const labelInterval = getXAxisLabelInterval();

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Daily Activity
        <span className="text-sm font-normal text-gray-500 ml-2">
          ({data.length} {data.length === 1 ? "day" : "days"})
        </span>
      </h3>

      <div className="overflow-x-auto">
        <svg
          width={width}
          height={height}
          className="mx-auto"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          {/* Y-axis grid lines and labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
            const value = maxValue * tick;
            const y = padding.top + chartHeight * (1 - tick);
            return (
              <g key={tick}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={width - padding.right}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray={tick === 0 ? "0" : "4 2"}
                />
                <text
                  x={padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#6b7280"
                >
                  ${Math.round(value).toLocaleString()}
                </text>
              </g>
            );
          })}

          {/* Issued line (blue) */}
          <path
            d={issuedPath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Redeemed line (green) */}
          <path
            d={redeemedPath}
            fill="none"
            stroke="#10b981"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points - invisible large circles for hover detection */}
          {data.map((item, index) => {
            const x = getX(index);
            const yIssued = getY(item.issuedValue);
            const yRedeemed = getY(item.redeemedValue);

            return (
              <g key={item.date}>
                {/* Issued point */}
                <circle
                  cx={x}
                  cy={yIssued}
                  r="12"
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredPoint({ x: rect.left, y: rect.top, data: item });
                  }}
                />
                <circle
                  cx={x}
                  cy={yIssued}
                  r="4"
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth="2"
                  style={{ pointerEvents: "none" }}
                />

                {/* Redeemed point */}
                <circle
                  cx={x}
                  cy={yRedeemed}
                  r="12"
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredPoint({ x: rect.left, y: rect.top, data: item });
                  }}
                />
                <circle
                  cx={x}
                  cy={yRedeemed}
                  r="4"
                  fill="#10b981"
                  stroke="white"
                  strokeWidth="2"
                  style={{ pointerEvents: "none" }}
                />
              </g>
            );
          })}

          {/* X-axis labels */}
          {data.map((item, index) => {
            if (index % labelInterval !== 0 && index !== data.length - 1) {
              return null;
            }

            const x = getX(index);
            return (
              <text
                key={`label-${item.date}`}
                x={x}
                y={height - padding.bottom + 20}
                textAnchor="middle"
                fontSize="11"
                fill="#6b7280"
                transform={`rotate(-45 ${x} ${height - padding.bottom + 20})`}
              >
                {formatDate(item.date, data.length)}
              </text>
            );
          })}

          {/* X-axis line */}
          <line
            x1={padding.left}
            y1={padding.top + chartHeight}
            x2={width - padding.right}
            y2={padding.top + chartHeight}
            stroke="#6b7280"
            strokeWidth="2"
          />

          {/* Y-axis line */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + chartHeight}
            stroke="#6b7280"
            strokeWidth="2"
          />
        </svg>
      </div>

      {/* Hover Tooltip */}
      {hoveredPoint && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: hoveredPoint.x + 15,
            top: hoveredPoint.y - 80,
          }}
        >
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
            <div className="font-semibold mb-1">
              {formatDate(hoveredPoint.data.date, data.length)}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>
                  Issued: {formatCurrency(hoveredPoint.data.issuedValue)}
                </span>
              </div>
              <div className="text-gray-300 text-[10px] ml-5">
                {hoveredPoint.data.issuedCount || 0} vouchers
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>
                  Redeemed: {formatCurrency(hoveredPoint.data.redeemedValue)}
                </span>
              </div>
              <div className="text-gray-300 text-[10px] ml-5">
                {hoveredPoint.data.redemptionCount} vouchers
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-1 bg-blue-500 rounded"></div>
          <span className="text-sm text-gray-700">Issued</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-1 bg-green-500 rounded"></div>
          <span className="text-sm text-gray-700">Redeemed</span>
        </div>
      </div>
    </div>
  );
}
